import { useState } from 'react';
import apiClient from '../../../../api/apiClient';
import { useToast } from '../../../../context/ToastContext';

export function usePayrollGenerator(
  selectedMonth: number,
  selectedYear: number,
  onSuccess: () => void
) {
  const { showSuccess, showError } = useToast();
  const [generating, setGenerating] = useState(false);

  const generatePayroll = async () => {
    setGenerating(true);
    try {
      // Delete existing draft payrolls
      const existingPayrolls = await apiClient.get('/hris/payroll', {
        params: { month: selectedMonth, year: selectedYear },
      });
      const existingData = existingPayrolls?.data || existingPayrolls;
      const drafts = (existingData?.payrolls || []).filter(p => p.status === 'draft');

      for (const p of drafts) {
        try {
          await apiClient.delete(`/hris/payroll/${p.id}`);
        } catch (err) {
          console.error('Error deleting draft:', err);
        }
      }

      // Get active employees
      const empResponse = await apiClient.get('/hris/employees');
      const activeEmployees = Array.isArray(empResponse?.employees || empResponse)
        ? (empResponse?.employees || empResponse).filter(e => e.status === 'active')
        : [];

      const payrollsToCreate = [];

      for (const emp of activeEmployees) {
        // Get approved overtime records for this employee and period
        let overtimeHours = 0;
        let overtimePay = 0;

        try {
          const overtimeResponse = await apiClient.get('/hris/overtime', {
            params: {
              month: selectedMonth,
              year: selectedYear,
              employee_id: emp.id,
            },
          });
          const overtimeData = overtimeResponse?.overtimes || overtimeResponse || [];
          const approvedOvertimes = Array.isArray(overtimeData)
            ? overtimeData.filter(ot => ot.status === 'approved')
            : [];

          if (approvedOvertimes.length > 0) {
            overtimeHours = approvedOvertimes.reduce(
              (sum, ot) => sum + (parseFloat(ot.duration_hours) || 0),
              0
            );
            overtimePay = approvedOvertimes.reduce(
              (sum, ot) => sum + (parseFloat(ot.total_amount) || 0),
              0
            );
          }
        } catch (err) {
          console.error('Error loading overtime:', err);
        }

        const baseSalary = parseFloat(emp.base_salary) || 0;
        const kpiScore = 0;
        const kpiBonus = 0;
        // TODO: Implement KPI results integration when endpoint is available

        const allowances = parseFloat(emp.allowances) || 0;
        const grossSalary = baseSalary + allowances + overtimePay + kpiBonus;

        // Calculate deductions
        const tax = Math.round(grossSalary * 0.05); // 5% PPh
        const bpjsEmployee = Math.round(baseSalary * 0.01); // 1% BPJS Kesehatan
        const bpjsCompany = Math.round(baseSalary * 0.04); // 4% BPJS Ketenagakerjaan

        const totalDeductions = tax + bpjsEmployee;
        const netSalary = grossSalary - totalDeductions;

        payrollsToCreate.push({
          employee_id: emp.id,
          month: selectedMonth,
          year: selectedYear,
          base_salary: baseSalary,
          allowances: allowances,
          overtime_pay: overtimePay,
          overtime_hours: Math.round(overtimeHours * 10) / 10,
          kpi_score: kpiScore,
          kpi_bonus: kpiBonus,
          bonus: 0,
          deductions: 0,
          tax: tax,
          bpjs_employee: bpjsEmployee,
          bpjs_company: bpjsCompany,
          gross_salary: grossSalary,
          net_salary: netSalary,
          status: 'draft',
        });
      }

      // Create payrolls
      for (const payroll of payrollsToCreate) {
        try {
          await apiClient.post('/hris/payroll', payroll);
        } catch (err) {
          console.error('Error creating payroll:', err);
        }
      }

      onSuccess();
      showSuccess(`Berhasil generate payroll untuk ${payrollsToCreate.length} pegawai!`);
    } catch (error) {
      console.error('Error generating payroll:', error);
      showError('Gagal generate payroll: ' + (error?.message || 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  return {
    generating,
    generatePayroll,
  };
}
